import { Box, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState, } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useGridApiRef } from '@mui/x-data-grid-pro'

import { apps, LOCAL_STORAGE_GRID_KEYS } from '../../../_constants'
import { getGridContextMenuList } from '../../../_helpers/getGridContextMenuList'
import { formatDate, getLocalStorageItem, setLocalStorageItem } from '../../../_helpers'
import { LabelColumnV2 } from '../../../components/DataGridV2/DataColumnsV2'
import DataGridV2 from '../../../components/DataGridV2'
import { getEventLogsByContactId } from '../../../apis/communicationApi'
import { useParams } from 'react-router-dom';
import { getMailgunEvent } from '../util'
import { INITIAL_ROW_SELECTION_MODEL } from '../../../components/DataGridV2/constants'

const EventLogsGrid = () => {
    const { t, i18n } = useTranslation();
    const { contactId } = useParams();
    const app = useSelector((state) => state.app);
    const keyPrefix = app === apps.aftercare ? "AFTERCARE" :  app === apps.huisinfo ? "BUYERS_GUIDE" : "GENERAL";
    const apiRef = useGridApiRef();
    const [rowsData, setRowsData] = useState([]);
    const [paginationModel, setPaginationModel] = useState(() => getLocalStorageItem(
        LOCAL_STORAGE_GRID_KEYS.COMMUNICATION_EVENT_LOGS_PAGINATION[keyPrefix], undefined
    ))
    const [isRowsDataLoading, setIsRowsDataLoading] = useState(false)
    const [rowSelectionModel, setRowSelectionModel] = useState(INITIAL_ROW_SELECTION_MODEL);

     useEffect(() => {
            (async () => {
                setIsRowsDataLoading(true);
                try {
                    const response = await getEventLogsByContactId(contactId);
                    if (response.status === 200) {
                        setRowsData(response.data)
                    }
                    setIsRowsDataLoading(false)
                } catch (err) {
                    console.log(err)
                    setIsRowsDataLoading(false);
                }
            })();
        }, [contactId]);

    const getOptions = getGridContextMenuList({
        filterKey: LOCAL_STORAGE_GRID_KEYS.COMMUNICATION_EVENT_LOGS_COLUMN_FILTER[keyPrefix],
        sortKey: LOCAL_STORAGE_GRID_KEYS.COMMUNICATION_EVENT_LOGS_COLUMN_SORT[keyPrefix],
        i18n,
        t,
        apiRef,
        mode: "client",
        getValueForCopy: (rowData, extraData) => {
              let text = ""
              switch (extraData.fieldName) {
                case "event":
                  text = t(`communication.mailgunEvent.${rowData.event}`)
                  break;
                case "timestamp":
                  text = formatDate(new Date(rowData.timestamp), true, "-")
                  break;
                default:
                  text = rowData[extraData.fieldName]
                  break;
              }
              return text
            }
    })

    const columns = useMemo(() => {
        const baseColumns = [
            {
                field: "timestamp",
                headerName: t('communication.dateAndTime'),
                flex: 1,
                minWidth: 90, 
                type: "dateTime",
                download: true,
                getApplyQuickFilterFn:(searchVal)=>{
                    return (value) => {
                        return formatDate(new Date(value), true, "-").includes(searchVal.toLowerCase())  
                    };
                },
                valueGetter: (params) => {
                    if (!params) return "";
                    return new Date(params);
                },
                renderCell: (params) => {
                    const rowData = params?.row;
                    return <LabelColumnV2 label={rowData?.timestamp ? formatDate(new Date(rowData?.timestamp), true, "-") : ""}
                        cellSubMenuOptions={{ options: getOptions({ field: "timestamp", isQuickFilterDisplay: rowData?.timestamp }), extraData: { fieldName: "timestamp", type: "dateTime" } }}
                        request={rowData}
                    />
                },
            },
            {
                field: "event",
                download: true,
                headerName: t('general.events'),
                flex: 1,
                minWidth: 90,
                type: "singleSelect",
                valueOptions: getMailgunEvent(t),
                renderCell: (params) => {
                    const rowData = params?.row;
                    if (!rowData) return "";
                    return (
                        <LabelColumnV2 label={t(`communication.mailgunEvent.${rowData.event}`)}
                            request={rowData}
                            cellSubMenuOptions={{ options: getOptions({ field: "event", isQuickFilterDisplay: Boolean(rowData.event.toString()) }), extraData: { fieldName: "event", type: "singleSelect" } }}
                        />
                    )
                },
            },
            {
                field: "sender",
                headerName: t('communication.sender'),
                flex: 1,
                minWidth: 90, 
                download: true,
                renderCell: (params) => {
                    const rowData = params?.row;
                    return <LabelColumnV2 label={rowData?.sender ? rowData?.sender : "-"}
                        cellSubMenuOptions={{ options: getOptions({ field: "sender", isQuickFilterDisplay: Boolean(rowData.sender) }), extraData: { fieldName: "sender" } }}
                        request={rowData}
                    />
                },
            },

            {
                field: "recipient",
                headerName: t('communication.recipient'),
                flex: 1,
                minWidth: 90, 
                download: true,
                renderCell: (params) => {
                    const rowData = params?.row;
                    return <LabelColumnV2 label={rowData?.recipient}
                        cellSubMenuOptions={{ options: getOptions({ field: "recipient", isQuickFilterDisplay: Boolean(rowData.recipient) }), extraData: { fieldName: "recipient" } }}
                        request={rowData}
                    />
                },
            },
            {
                field: "status",
                headerName: t('general.status'),
                flex: 1,
                minWidth: 90, 
                download: true,
                renderCell: (params) => {
                    const rowData = params?.row;
                    return <LabelColumnV2 label={rowData?.status ? rowData?.status : "-"}  tooltipLabel={rowData?.status}
                        cellSubMenuOptions={{ options: getOptions({ field: "status", isQuickFilterDisplay: Boolean(rowData.status) }), extraData: { fieldName: "status" } }}
                        request={rowData}
                    />
                },
            },

        ];
        return baseColumns
    }, [])

    const onPaginationModelChange = useCallback((newPaginationModel) => {
        setPaginationModel(newPaginationModel)
        setLocalStorageItem(
            LOCAL_STORAGE_GRID_KEYS.COMMUNICATION_EVENT_LOGS_PAGINATION[keyPrefix],
            newPaginationModel
        );
    }, [keyPrefix])

   const getTimeLineForPrintAndDownload = ({ isAllSelected = true,idsToExport=[] }) => {
        const userSelectedRows = isAllSelected ? rowsData : idsToExport?.map((id) => rowsData.find(item => item?.id === id));
        const results = userSelectedRows?.map((item) => {
            const result = { ...item }
            Object.keys(item).forEach((key) => {
                if (key === "event") {
                    result[key] = t(`communication.mailgunEvent.${item[key]}`);
                } else if (key === "timestamp") {
                    result[key] = formatDate(new Date(item[key]), true, "-")
                } else {
                    result[key] = item[key] ? item[key] : "-"
                }
            });
            return result
        })
        return results
    }

    return (
        <Box width={"100%"}>
            <Typography
                variant="h6"
                sx={{
                    px: 2,
                    py: 1.5
                }}>
                {t('general.timeline')}
            </Typography>
            <Box >
                <DataGridV2
                    sx={{  height: 400, p:1}}
                    localSearchIdentifier={LOCAL_STORAGE_GRID_KEYS.COMMUNICATION_EVENT_LOGS_SEARCH_TEXT[keyPrefix]}
                    localColumnFilterIdentifier={LOCAL_STORAGE_GRID_KEYS.COMMUNICATION_EVENT_LOGS_COLUMN_FILTER[keyPrefix]}
                    localColumnSortIdentifier={LOCAL_STORAGE_GRID_KEYS.COMMUNICATION_EVENT_LOGS_COLUMN_SORT[keyPrefix]}
                    localColumnOrderIdentifier={LOCAL_STORAGE_GRID_KEYS.COMMUNICATION_EVENT_LOGS_COLUMN_ORDER[keyPrefix]}
                    localColumnWidthIdentifier={LOCAL_STORAGE_GRID_KEYS.COMMUNICATION_EVENT_LOGS_COLUMN_WIDTH[keyPrefix]}
                    localColumnVisibilityIdentifier={LOCAL_STORAGE_GRID_KEYS.COMMUNICATION_EVENT_LOGS_COLUMN_VISIBILITY[keyPrefix]}
                    apiRef={apiRef}
                    loading={isRowsDataLoading}
                    checkboxSelection
                    rows={rowsData}
                    columns={columns}
                    pagination
                    {...(!!paginationModel && { paginationModel })}
                    onPaginationModelChange={onPaginationModelChange}
                    toolbarProps={{
                        title: t("general.timeline"),
                        selectedRows: rowSelectionModel,
                        getData: getTimeLineForPrintAndDownload,
                    }}
                    filterMode="client"
                    onRowSelectionModelChange={(newRowSelectionModel) => {
                        setRowSelectionModel(newRowSelectionModel);
                    }}
                />
            </Box>
        </Box>
    )
}

export default EventLogsGrid